import React from 'react';
import { Paper, Box, Stack, Typography, IconButton, Grid, Tooltip } from '@mui/material';
import { Rnd } from 'react-rnd';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import WebAssetIcon from '@mui/icons-material/WebAsset';
import MinimizeIcon from '@mui/icons-material/Minimize';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { IJoinedRoomState } from '../../../Core/State/IJoinedRoomState';

type DebugInfoData = {
    [key: string]: string | number | Array<{
        amount: number;
        eligiblePlayers: string[];
    }>;
};

type DebugInfo = {
    'Room Info': DebugInfoData;
    'Game State': DebugInfoData & {
        cards?: string;
    };
    'Pot Info': DebugInfoData;
    'Round Info': DebugInfoData;
    'Current Turn': {
        player: string;
        actions: Array<{
            type: string;
            minAmount?: number;
            maxAmount?: number;
        }>;
        timeLeft?: number;
    };
    'Players': Array<{
        name: string;
        bal: string;
        bet: string;
        status: string;
        cards: string;
        state: string;
        subState: string;
        idle: string;
        missed: number;
    }>;
};

interface DebugPanelProps {
    show: boolean;
    onClose: () => void;
    roomState: IJoinedRoomState;
    currentUsername: string;
    isPaused: boolean;
    onPauseToggle: () => void;
}

interface DebugPanelState {
    width: number;
    height: number;
    x: number;
    y: number;
    pausedDebugInfo: DebugInfo | null;
}

export class DebugPanel extends React.Component<DebugPanelProps, DebugPanelState> {
    private readonly SIZE_PRESETS = {
        min: { width: 650, height: 400 },
        medium: { width: 850, height: 500 },
        full: { width: 1200, height: 900 }
    };

    constructor(props: DebugPanelProps) {
        super(props);
        this.state = {
            width: 850,
            height: 500,
            x: 20,
            y: 20,
            pausedDebugInfo: null
        };
    }

    componentDidUpdate(prevProps: DebugPanelProps) {
        if (prevProps.isPaused !== this.props.isPaused) {
            if (this.props.isPaused) {
                this.setState({ pausedDebugInfo: this.getDebugInfo() });
            } else {
                this.setState({ pausedDebugInfo: null });
            }
        }
    }

    private getDebugInfo(): DebugInfo {
        const {
            roomId, gameId, roundName, pot, currentBet, activePlayerId,
            dealerId, smallBlindId, bigBlindId, turnTime, currentTurnStartTime,
            playerSatCount, gameType, secondsUntilNextGame, playersActedThisRound,
            shouldEndRound, shouldShowdown, consecutiveGamesCount, stateName,
            showdown, potBeforeDistribution, roundingDust, communityCards,
            availableActions
        } = this.props.roomState;

        const activePlayer = this.props.roomState.players.find(p => p.userId === activePlayerId);

        return {
            'Room Info': {
                id: roomId || 'N/A',
                game: gameId || 'N/A',
                type: gameType,
                stakes: `$${this.props.roomState.smallBlind}/$${this.props.roomState.bigBlind}`,
                min: `$${this.props.roomState.minBuyIn}`,
                max: `$${this.props.roomState.maxBuyIn}`,
                players: `${playerSatCount}/${this.props.roomState.maxPlayers}`
            },
            'Game State': {
                state: stateName || 'N/A',
                round: roundName || 'N/A',
                showdown: showdown ? 'YES' : 'NO',
                nextIn: secondsUntilNextGame ? `${secondsUntilNextGame}s` : 'N/A',
                games: consecutiveGamesCount,
                active: activePlayer ? activePlayer.username : 'N/A',
                cards: communityCards?.length ? communityCards.join(' ') : 'No cards'
            },
            'Pot Info': {
                pot: `$${this.props.roomState.pot || 0}`,
                currentBet: `$${this.props.roomState.currentBet || 0}`,
                potBeforeDistribution: this.props.roomState.potBeforeDistribution ? `$${this.props.roomState.potBeforeDistribution}` : 'N/A',
                roundingDust: this.props.roomState.roundingDust ? `$${this.props.roomState.roundingDust}` : 'N/A',
            },
            'Round Info': {
                acted: playersActedThisRound || 0,
                shouldEnd: shouldEndRound ? 'YES' : 'NO',
                shouldShow: shouldShowdown ? 'YES' : 'NO',
                turnTime: `${turnTime}s`,
                turnStarted: currentTurnStartTime ? new Date(currentTurnStartTime).toLocaleTimeString() : 'N/A'
            },
            'Current Turn': {
                player: activePlayer ? activePlayer.username : 'N/A',
                actions: availableActions?.actions || [],
                timeLeft: turnTime
            },
            'Players': this.props.roomState.players.map(p => ({
                name: p.username,
                bal: `$${p.balance || 0}`,
                bet: p.currentBet ? `$${p.currentBet}` : '-',
                status: [
                    p.hasFolded ? 'F' : '',
                    p.isSitOut ? 'O' : '',
                    p.userId === activePlayerId ? '➤' : '',
                    p.userId === dealerId ? 'D' : '',
                    p.userId === smallBlindId ? 'S' : '',
                    p.userId === bigBlindId ? 'B' : ''
                ].filter(Boolean).join(''),
                cards: p.cards?.join(',') || '-',
                state: p.status,
                subState: p.subStatus,
                idle: p.isIdle ? 'YES' : 'NO',
                missed: p.missedTurnsCount || 0
            }))
        };
    }

    private handleSizePreset = (preset: 'min' | 'medium' | 'full') => {
        this.setState({
            width: this.SIZE_PRESETS[preset].width,
            height: this.SIZE_PRESETS[preset].height
        });
    };

    private handleCopyToClipboard = () => {
        try {
            const debugInfo = this.props.isPaused ? 
                this.state.pausedDebugInfo || this.getDebugInfo() : 
                this.getDebugInfo();

            const jsonData = JSON.stringify(debugInfo, null, 2);
            
            // Create a temporary textarea element
            const textArea = document.createElement('textarea');
            textArea.value = jsonData;
            textArea.style.position = 'fixed';  // Avoid scrolling to bottom
            textArea.style.left = '0';
            textArea.style.top = '0';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            
            // Select and copy
            textArea.select();
            document.execCommand('copy');
            
            // Cleanup
            document.body.removeChild(textArea);
            
            // Optional: Add visual feedback
            const copyButton = document.querySelector('[data-copy-button]');
            if (copyButton) {
                const originalColor = copyButton.getAttribute('style');
                copyButton.setAttribute('style', 'color: #00ff00 !important');
                setTimeout(() => {
                    copyButton.setAttribute('style', originalColor || '');
                }, 500);
            }
        } catch (error) {
            console.error('Failed to copy debug info:', error);
        }
    };

    render() {
        if (!this.props.show) return null;

        const debugInfo = this.props.isPaused ? 
            this.state.pausedDebugInfo || this.getDebugInfo() : 
            this.getDebugInfo();

        return (
            <Rnd
                size={{ width: this.state.width, height: this.state.height }}
                position={{ x: this.state.x, y: this.state.y }}
                onDragStop={(e, d) => {
                    this.setState({ x: d.x, y: d.y });
                }}
                onResize={(e, direction, ref, delta, position) => {
                    this.setState({
                        width: ref.offsetWidth,
                        height: ref.offsetHeight,
                        ...position,
                    });
                }}
                minWidth={650}
                minHeight={400}
                maxWidth={1200}
                maxHeight={900}
                dragHandleClassName="debug-handle"
                bounds="window"
                style={{ zIndex: 999999 }}
            >
                <Paper
                    sx={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(26, 26, 26, 0.95)',
                        backdropFilter: 'blur(5px)',
                        borderRadius: 1,
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{ 
                        width: '100%', 
                        height: '100%',
                        color: '#fff',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {/* Header - Draggable area */}
                        <Stack 
                            direction="row" 
                            justifyContent="space-between" 
                            alignItems="center" 
                            sx={{ 
                                p: 0.5,
                                backgroundColor: 'rgba(0,0,0,0.3)',
                                cursor: 'move'
                            }}
                            className="debug-handle"
                        >
                            {/* Title on the left */}
                            <Typography sx={{ color: '#00ff00', fontSize: '0.9rem', fontWeight: 'bold', pl: 1 }}>
                                Debug Panel
                            </Typography>

                            {/* All controls on the right */}
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                {/* Add Copy button before size presets */}
                                <Tooltip title="Copy debug info to clipboard">
                                    <IconButton 
                                        size="small"
                                        onClick={this.handleCopyToClipboard}
                                        data-copy-button
                                        sx={{ 
                                            color: '#666',
                                            '&:hover': { color: '#999' }
                                        }}
                                    >
                                        <ContentCopyIcon sx={{ fontSize: '1rem' }} />
                                    </IconButton>
                                </Tooltip>

                                {/* Size preset buttons */}
                                <Tooltip title="Minimum size">
                                    <IconButton 
                                        size="small"
                                        onClick={() => this.handleSizePreset('min')}
                                        sx={{ 
                                            color: '#666',
                                            '&:hover': { color: '#999' }
                                        }}
                                    >
                                        <MinimizeIcon sx={{ fontSize: '1rem' }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Medium size">
                                    <IconButton 
                                        size="small"
                                        onClick={() => this.handleSizePreset('medium')}
                                        sx={{ 
                                            color: '#666',
                                            '&:hover': { color: '#999' }
                                        }}
                                    >
                                        <WebAssetIcon sx={{ fontSize: '1rem' }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Full size">
                                    <IconButton 
                                        size="small"
                                        onClick={() => this.handleSizePreset('full')}
                                        sx={{ 
                                            color: '#666',
                                            '&:hover': { color: '#999' }
                                        }}
                                    >
                                        <CropSquareIcon sx={{ fontSize: '1rem' }} />
                                    </IconButton>
                                </Tooltip>

                                {/* Pause/Play button */}
                                <IconButton 
                                    size="small"
                                    onClick={this.props.onPauseToggle}
                                    sx={{ 
                                        color: this.props.isPaused ? '#ff9900' : '#00ff00',
                                        '&:hover': { 
                                            color: this.props.isPaused ? '#ffaa00' : '#00ff66'
                                        }
                                    }}
                                >
                                    {this.props.isPaused ? 
                                        <PlayArrowIcon sx={{ fontSize: '1.2rem' }} /> : 
                                        <PauseIcon sx={{ fontSize: '1.2rem' }} />
                                    }
                                </IconButton>

                                {/* Close button */}
                                <IconButton 
                                    size="small"
                                    onClick={this.props.onClose}
                                    sx={{ 
                                        color: '#666',
                                        '&:hover': { color: '#ff4444' }
                                    }}
                                >
                                    <CloseIcon sx={{ fontSize: '1rem' }} />
                                </IconButton>
                            </Stack>
                        </Stack>

                        {/* Content */}
                        <Box sx={{ 
                            p: 2, 
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            flexGrow: 1,
                            '&::-webkit-scrollbar': {
                                width: '6px',
                                backgroundColor: 'transparent'
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'rgba(128, 128, 128, 0.3)',
                                borderRadius: '3px',
                                '&:hover': {
                                    backgroundColor: 'rgba(128, 128, 128, 0.5)'
                                }
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: 'transparent'
                            }
                        }}>
                            <Grid 
                                container 
                                spacing={2} 
                                sx={{ width: '100%', m: 0 }}
                            >
                                {/* First row: Room Info, Game State, Pot Info, Round Info */}
                                {Object.entries(debugInfo).slice(0, 4).map(([section, data]) => (
                                    <Grid item xs={6} key={section}>
                                        <Box sx={{ 
                                            p: 1.5,
                                            backgroundColor: 'rgba(0,0,0,0.2)',
                                            borderRadius: 1,
                                            width: '100%'
                                        }}>
                                            <Typography sx={{ 
                                                color: '#00ff00', 
                                                fontSize: '0.9rem', 
                                                mb: 0.5,
                                                borderBottom: '1px solid rgba(255,255,255,0.1)',
                                                px: 1
                                            }}>
                                                {section}
                                            </Typography>
                                            {Object.entries(data).map(([key, value]) => (
                                                <Typography key={key} sx={{ 
                                                    fontSize: '0.85rem', 
                                                    color: '#ccc', 
                                                    lineHeight: '1.2',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    px: 1
                                                }}>
                                                    <span style={{ 
                                                        color: '#888', 
                                                        width: '80px', 
                                                        display: 'inline-block',
                                                        marginRight: '8px'
                                                    }}>
                                                        {key}:
                                                    </span>
                                                    <span style={{ color: '#66ff66' }}>{value}</span>
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Grid>
                                ))}

                                {/* Current Turn section - now full width */}
                                <Grid item xs={12}>
                                    {debugInfo['Current Turn'] && (
                                        <Box sx={{ 
                                            p: 1.5,
                                            backgroundColor: 'rgba(0,0,0,0.2)',
                                            borderRadius: 1,
                                            width: '100%'
                                        }}>
                                            <Typography sx={{ 
                                                color: '#00ff00', 
                                                fontSize: '0.9rem', 
                                                mb: 0.5,
                                                borderBottom: '1px solid rgba(255,255,255,0.1)'
                                            }}>
                                                Current Turn
                                            </Typography>
                                            <Box sx={{ p: 1 }}>
                                                <Typography sx={{ 
                                                    fontSize: '0.85rem', 
                                                    color: '#ccc',
                                                    fontFamily: 'monospace',
                                                    mb: 1
                                                }}>
                                                    <span style={{ color: '#ff9' }}>Player: </span>
                                                    <span style={{ color: '#0f0' }}>{debugInfo['Current Turn'].player}</span>
                                                    {debugInfo['Current Turn'].timeLeft && (
                                                        <>
                                                            <span style={{ 
                                                                color: '#ff9', 
                                                                marginLeft: '16px'
                                                            }}> | Time Left: </span>
                                                            <span style={{ color: '#0f0' }}>{debugInfo['Current Turn'].timeLeft}s</span>
                                                        </>
                                                    )}
                                                </Typography>
                                                {debugInfo['Current Turn'].actions.length > 0 ? (
                                                    <Typography sx={{ 
                                                        fontSize: '0.85rem', 
                                                        color: '#ccc',
                                                        fontFamily: 'monospace'
                                                    }}>
                                                        <span style={{ color: '#ff9' }}>Available Actions:</span>
                                                        <Box component="ul" sx={{ mt: 0.5, pl: 2 }}>
                                                            {debugInfo['Current Turn'].actions.map((action, idx) => (
                                                                <li key={idx} style={{ color: '#0f0' }}>
                                                                    {action.type}
                                                                    {(action.minAmount !== undefined || action.maxAmount !== undefined) && (
                                                                        <span style={{ color: '#888' }}>
                                                                            {' '}(
                                                                            {action.minAmount !== undefined && `min: $${action.minAmount}`}
                                                                            {action.minAmount !== undefined && action.maxAmount !== undefined && ' | '}
                                                                            {action.maxAmount !== undefined && `max: $${action.maxAmount}`}
                                                                            )
                                                                        </span>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </Box>
                                                    </Typography>
                                                ) : (
                                                    <Typography sx={{ 
                                                        fontSize: '0.85rem', 
                                                        color: '#666',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        No actions available
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                </Grid>

                                {/* Players section */}
                                <Grid item xs={12}>
                                    <Box sx={{ 
                                        p: 1.5,
                                        backgroundColor: 'rgba(0,0,0,0.2)',
                                        borderRadius: 1,
                                        width: '100%'
                                    }}>
                                        <Typography sx={{ 
                                            color: '#00ff00', 
                                            fontSize: '0.9rem', 
                                            mb: 0.5,
                                            borderBottom: '1px solid rgba(255,255,255,0.1)'
                                        }}>
                                            Players
                                        </Typography>
                                        <Grid container spacing={0.5}>
                                            {debugInfo.Players.map((player, idx) => (
                                                <Grid item xs={12} md={6} key={idx}>
                                                    <Box sx={{
                                                        p: 0.5,
                                                        px: 2,
                                                        backgroundColor: 'rgba(0,0,0,0.1)',
                                                        borderRadius: 1,
                                                        mb: 0.5,
                                                        border: player.name === this.props.currentUsername ? 
                                                            '1px solid rgba(255,255,0,0.3)' : 
                                                            '1px solid transparent'
                                                    }}>
                                                        <Typography sx={{ 
                                                            fontSize: '0.85rem', 
                                                            color: '#ccc',
                                                            fontFamily: 'monospace',
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-word'
                                                        }}>
                                                            {player.status && (
                                                                <span style={{ 
                                                                    color: player.status.includes('➤') ? '#0f0' : '#ff9',
                                                                    fontWeight: player.status.includes('➤') ? 'bold' : 'normal'
                                                                }}>
                                                                    [{player.status}] 
                                                                </span>
                                                            )}
                                                            <span style={{ 
                                                                color: player.name === this.props.currentUsername ? '#ff0' : '#6ff',
                                                                fontWeight: player.name === this.props.currentUsername ? 'bold' : 'normal'
                                                            }}>
                                                                {player.name}
                                                            </span>
                                                            <br />
                                                            Bal: {player.bal} | Bet: {player.bet} | Cards: {player.cards}
                                                            <br />
                                                            State: {player.state}/{player.subState}
                                                            <br />
                                                            Idle: {player.idle} | Missed: {player.missed}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                </Grid>

                                {/* Add this new section for Pot Info */}
                                <Grid item xs={12}>
                                    <Box sx={{ 
                                        p: 1.5,
                                        backgroundColor: 'rgba(0,0,0,0.2)',
                                        borderRadius: 1
                                    }}>
                                        <Typography sx={{ 
                                            color: '#00ff00', 
                                            fontSize: '0.9rem', 
                                            mb: 0.5,
                                            borderBottom: '1px solid rgba(255,255,255,0.1)'
                                        }}>
                                            Pot Information
                                        </Typography>
                                        <Box sx={{ pl: 2 }}>
                                            {Object.entries(debugInfo['Pot Info']).map(([key, value]) => (
                                                <Typography key={key} sx={{ 
                                                    fontSize: '0.85rem', 
                                                    color: '#ccc',
                                                    fontFamily: 'monospace'
                                                }}>
                                                    <span style={{ color: '#ff9' }}>{key}: </span>
                                                    <span style={{ color: '#0f0' }}>
                                                        {typeof value === 'string' || typeof value === 'number' 
                                                            ? value.toString()
                                                            : Array.isArray(value) 
                                                                ? JSON.stringify(value)
                                                                : 'N/A'}
                                                    </span>
                                                </Typography>
                                            ))}

                                            <Typography sx={{ 
                                                fontSize: '0.85rem', 
                                                color: '#ff9',
                                                fontFamily: 'monospace',
                                                mt: 1
                                            }}>
                                                Sidepots:
                                            </Typography>
                                            {this.props.roomState.sidePots && this.props.roomState.sidePots.length > 0 ? (
                                                <Box sx={{ pl: 2 }}>
                                                    {this.props.roomState.sidePots.map((pot, index) => (
                                                        <Box key={index} sx={{ 
                                                            mb: 1, 
                                                            p: 1, 
                                                            backgroundColor: 'rgba(0,0,0,0.2)',
                                                            borderRadius: '4px'
                                                        }}>
                                                            <Typography sx={{ 
                                                                fontSize: '0.85rem', 
                                                                color: '#0f0',
                                                                fontFamily: 'monospace'
                                                            }}>
                                                                Pot {index + 1}: ${pot.amount}
                                                            </Typography>
                                                            <Typography sx={{ 
                                                                fontSize: '0.85rem', 
                                                                color: '#ccc',
                                                                fontFamily: 'monospace',
                                                                pl: 2
                                                            }}>
                                                                Eligible Players: {pot.eligiblePlayers.map((playerId: string) => {
                                                                    const player = this.props.roomState.players.find(p => p.userId === playerId);
                                                                    return player ? player.username : playerId;
                                                                }).join(', ')}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            ) : (
                                                <Typography sx={{ color: '#ccc', pl: 2 }}>No sidepots</Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Paper>
            </Rnd>
        );
    }
} 
